<template>
  <div
    ref="sizeGuide"
    class="size-guide"
  >
    <size-guide-content
      v-if="sizeGuideContext === 'general'"
      :accordion-blocks="accordionBlocks"
      :categories="categories"
      :context="sizeGuideContext"
      :general-category="generalCategory"
      :is-mobile="isMobile"
      :locales="locales"
      :product-type="productType"
      :section-id="liquid.sectionId"
      :unit-types="unitTypes"
    />

    <overlay
      v-else
      :is-active="showOverlay"
      parent-class="size-guide-overlay"
      @close-overlay="closeOverlay"
    >
      <template #body>
        <header
          class="size-guide-overlay__header overlay__header"
          js-overlay="headerElement"
        >
          <button
            class="size-guide-overlay__close overlay__close"
            :aria-label="closeOverlayText"
            type="button"
            @click.prevent="closeOverlay"
          >
            <icon-close />
          </button>
        </header>

        <div
          class="overlay__content size-guide-overlay__content"
          js-overlay="scrollElement"
        >
          <div class="size-guide-overlay__guides">
            <size-guide-content
              :accordion-blocks="accordionBlocks"
              :categories="categories"
              :context="sizeGuideContext"
              :general-category="generalCategory"
              :is-mobile="isMobile"
              :locales="locales"
              :product-type="productType"
              :section-id="liquid.sectionId"
              :unit-types="unitTypes"
            />
          </div>
        </div>
      </template>
    </overlay>
  </div>
</template>

<script>

/**
 * Section: Size Guide
 * ------------------------------------------------------------------------------
 * @param {String} [liquid.sectionId] - Section's Liquid ID.
 * @param {Array}  [accordionBlocks] - Formatted block objects which contain title and contents for accordions.
 * @param {Array}  [categories] - Categories blocks which contain title and synonyms strings.
 * @param {String} [generalCategory] - General category string to use on size-guide page templates.
 * @param {String} [locales] - A string containing a list of locales separated by a comma.
 * @param {String} [unitTypes] - A string containing a list of unit types separated by a comma.
 * @param {String} [productType] - The type of current product used to predefine category value.
 */
import { mapState } from 'pinia'

import { useScreenStore } from '~stores/screen'

import { importComponent } from '~/core/mint/helpers'

import SizeGuideContent from '~components/size-guide/size-guide-content/size-guide-content'

import IconClose from '~icons/misc/close.svg'

export default {
  name: 'SizeGuide',

  components: {
    IconClose,
    Overlay: importComponent('overlay'),
    SizeGuideContent,
  },

  props: {
    accordionBlocks: {
      type: [Boolean, Array],
      default: false,
    },
    liquid: {
      type: Object,
      default: () => ({
        sectionId: '',
      }),
    },
    categories: {
      type: Array,
      default: () => [],
    },
    generalCategory: {
      type: String,
      default: 'General',
    },
    locales: {
      type: String,
      default: 'UK, EU, US',
    },
    unitTypes: {
      type: String,
      default: 'cm, in',
    },
    productType: {
      type: String,
      default: '',
    },
  },

  data() {
    return {
      showOverlay: false,
    }
  },

  computed: {

    /**
     * Map global state.
     */
    ...mapState(useScreenStore, {
      isMobile: 'mobile',
    }),

    /**
     * Calculate the 'Close' button text.
     * @returns {String}
     */
    closeOverlayText() {
      return mint.strings.accessibility.close
    },

    /**
     * Calculates context for size-chart table depending on the template.
     *
     * @returns {string} - either `product` or `general`
     */
    sizeGuideContext() {
      return mint.request.pageType === 'product' ? 'product' : 'general'
    },
  },

  mounted() {

    /**
     * Event triggered from the Product Form component.
     * - Typically will only be one 'Size Guide' button per-page,
     *   but if this were to change, pass the handle in the event or use Pinia.
     */
    mint.EventBus.on('product:openSizeGuide', () => {
      this.showOverlay = true
    })
  },

  methods: {

    /**
     * Close the overlay.
     */
    closeOverlay() {
      this.showOverlay = false
    },
  },
}
</script>

<style lang="scss">
  @import './size-guide.scss';
  @import '~components/size-guide/size-guide-content/size-guide-content';
</style>
